/**
 * Date formatting utilities for Kiosk Sessions API
 * Handles conversion between frontend Date objects and API YYYY-MM-DD format
 */

/**
 * Converts a Date object to YYYY-MM-DD format for display and internal use
 */
export function toApiDateFormat(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
}

/**
 * Converts a local date to UTC ISO string representing start of that day in user's timezone.
 * e.g., March 17 in UTC+2 → "2026-03-16T22:00:00Z"
 */
export function toUtcStartOfDay(date: Date): string {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  return local.toISOString();
}

/**
 * Converts a local date to UTC ISO string representing end of that day in user's timezone.
 * e.g., March 24 in UTC+2 → "2026-03-24T21:59:59Z"
 */
export function toUtcEndOfDay(date: Date): string {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
  return local.toISOString();
}

/**
 * Converts a Date object to YYYY-MM-DD format for end date filters.
 * Now returns same-day YYYY-MM-DD (no +1 day hack) — UTC conversion is handled by toUtcEndOfDay.
 */
export function toApiEndDateFormat(date: Date): string {
  return toApiDateFormat(date);
}

/**
 * Converts YYYY-MM-DD format from API to Date object
 * Also handles DD/MM/YYYY or DD.MM.YYYY format as fallback
 */
export function fromApiDateFormat(dateString: string): Date {
  if (!dateString) {
    throw new Error('Date string is required');
  }

  // First try YYYY-MM-DD format (API format)
  if (isValidApiDateFormat(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Fallback: try DD/MM/YYYY or DD.MM.YYYY format (display format)
  if (dateString.includes('/') || dateString.includes('.')) {
    try {
      return fromDisplayDateFormat(dateString);
    } catch (error) {
      // If display format also fails, throw original error
    }
  }

  throw new Error(`Invalid date format: ${dateString}. Expected YYYY-MM-DD`);
}

/**
 * Converts DD.MM.YYYY or DD/MM/YYYY format to Date object
 */
export function fromDisplayDateFormat(dateString: string): Date {
  // Handle both DD.MM.YYYY and DD/MM/YYYY formats
  const separator = dateString.includes('.') ? '.' : '/';
  const parts = dateString.split(separator);
  
  if (parts.length !== 3) {
    throw new Error(`Invalid date format: ${dateString}. Expected DD.MM.YYYY or DD/MM/YYYY`);
  }
  
  const [day, month, year] = parts.map(Number);
  
  // Basic validation
  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    throw new Error(`Invalid date format: ${dateString}. Expected DD.MM.YYYY or DD/MM/YYYY`);
  }
  
  if (year < 1900 || year > 2100) {
    throw new Error(`Invalid year: ${year}. Expected between 1900 and 2100`);
  }
  
  if (month < 1 || month > 12) {
    throw new Error(`Invalid month: ${month}. Expected between 1 and 12`);
  }
  
  if (day < 1 || day > 31) {
    throw new Error(`Invalid day: ${day}. Expected between 1 and 31`);
  }
  
  return new Date(year, month - 1, day);
}

/**
 * Validates if a string matches YYYY-MM-DD format
 */
export function isValidApiDateFormat(dateString: string): boolean {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) {
    return false;
  }
  
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Basic validation
  if (year < 1900 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  
  // More precise validation using Date
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && 
         date.getMonth() === month - 1 && 
         date.getDate() === day;
}

/**
 * Gets the start of the last year (one year ago from today)
 */
export function getStartOfLastYear(): Date {
  const today = new Date();
  const lastYear = new Date(today);
  lastYear.setFullYear(today.getFullYear() - 1);
  return lastYear;
}

/**
 * Gets today's date
 */
export function getToday(): Date {
  return new Date();
}

/**
 * Gets default date range (last year until today) in YYYY-MM-DD API format
 */
export function getDefaultDateRange(): { from: string; to: string } {
  const startOfLastYear = getStartOfLastYear();
  const today = getToday();

  return {
    from: toApiDateFormat(startOfLastYear),
    to: toApiDateFormat(today)
  };
}